import { motion } from "framer-motion";
import { History, ReceiptText, Wrench, Award, BadgeCheck } from "lucide-react";
import {
  fadeUp,
  revealLeft,
  revealRight,
  Eyebrow,
  TextCta,
  VIEWPORT,
  VIEWPORT_SM,
  staggerContainer,
} from "./primitives";
import { useLanguage } from "@/i18n/LanguageContext";

const DOSSIER_ICONS = [History, ReceiptText, Wrench, Award, BadgeCheck];

const STEP_IDS = ["registry-step-deposit", "registry-step-anchor", "registry-step-verify"];

/** Une étape du registre : numéro doré, titre, explication. */
const Step = ({ index, title, children, testId }) => (
  <motion.div variants={fadeUp} data-testid={testId}>
    <span className="mb-4 block font-serif text-sm tracking-[0.3em] text-gold-ink">
      {String(index + 1).padStart(2, "0")}
    </span>
    <h3 className="font-serif text-xl md:text-2xl mb-3">{title}</h3>
    <p className="text-sm md:text-base text-ink-muted leading-relaxed">{children}</p>
  </motion.div>
);

/**
 * Le registre en un seul bloc : ce qu'on dépose, ce que la montre reçoit,
 * ce qu'un tiers peut vérifier.
 *
 * Remplace quatre sections (réassurance, provenance, avantages, charte) qui
 * occupaient 40 % de l'accueil pour redire trois fois la même promesse sous
 * trois habillages différents. La liste des pièces au dossier est conservée,
 * c'est le seul élément concret des quatre ; la clause de mission ferme le
 * bloc, seule ligne de la charte qui engageait vraiment.
 */
export const RegistrySection = ({ registerTo }) => {
  const { t } = useLanguage();
  const steps = t("registry.steps");
  const dossier = t("registry.dossier");
  if (!Array.isArray(steps) || !Array.isArray(dossier)) return null;

  return (
    <section
      id="provenance"
      data-testid="registry-section"
      className="relative scroll-mt-28 py-16 md:py-32 border-t border-ink/5 bg-surface-muted overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={fadeUp}
          className="max-w-2xl mb-12 md:mb-16"
        >
          <Eyebrow>{t("registry.eyebrow")}</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mt-4">
            {t("registry.title")}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-start">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_SM}
            variants={staggerContainer}
            className="space-y-10"
          >
            {steps.map((step, i) => (
              <Step key={STEP_IDS[i]} index={i} title={step.title} testId={STEP_IDS[i]}>
                {step.description}
              </Step>
            ))}
          </motion.div>

          {/* Les pièces au dossier : la seule chose que le visiteur peut
              se représenter concrètement avant de s'inscrire. */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT}
            variants={revealRight}
            className="border border-ink/10"
            data-testid="registry-dossier"
          >
            <div className="border-b border-gold/20 px-6 py-3">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-ink">
                {t("registry.dossierLabel")}
              </span>
            </div>
            <ul className="divide-y divide-ink/5">
              {dossier.map((text, i) => {
                const Icon = DOSSIER_ICONS[i] || BadgeCheck;
                const isLast = i === dossier.length - 1;
                return (
                  <li key={text} className="flex items-center gap-4 px-6 py-4">
                    <span className="w-7 shrink-0 font-serif text-gold-gradient engraved text-sm tracking-[0.08em]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon size={18} strokeWidth={1.5} className="shrink-0 text-gold-ink" />
                    <span
                      className={`text-sm md:text-base ${
                        isLast ? "font-medium text-gold-ink" : "text-ink-body"
                      }`}
                    >
                      {text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={revealLeft}
          className="mt-16 md:mt-20 text-center"
        >
          <span aria-hidden="true" className="mx-auto mb-8 block h-px w-16 bg-gold/50" />
          <p
            data-testid="registry-mission"
            className="font-serif text-xl md:text-[1.7rem] leading-snug text-ink max-w-3xl mx-auto"
          >
            {t("registry.mission")}
          </p>
          <div className="mt-10 flex justify-center">
            <TextCta to={registerTo} track="registry" testId="registry-cta-link">
              {t("finalCta.register")}
            </TextCta>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RegistrySection;
